import React from 'react';
import classNames, { Argument } from 'classnames';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { Popover as HeadlessPopover, PopoverButton, PopoverPanel } from '@headlessui/react';

import { Country } from 'modules/refData/types/Countries';

import { useSelector } from 'hooks';
import { isLocalKiosk } from 'modules/dealers/selectors';

import Spinner from '../Spinner';
import CountryListContent from './Content';

export interface PopoverLayoutProps {
  className?: Argument;
  error?: React.ReactNode | boolean;
  countries: Country[];
  handleClick: (country: Country, close: () => void) => () => void;
  handleSearch: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isLoading: boolean;
  label?: React.ReactNode;
  searchText: string;
  setSearchText: (text: string) => void;
  value?: string;
}

const PopoverStateWatcher: React.FC<{ open: boolean; onOpen: () => void }> = ({ open, onOpen }) => {
  const prevOpen = React.useRef(open);

  React.useEffect(() => {
    if (open && !prevOpen.current) {
      onOpen();
    }
    prevOpen.current = open;
  }, [open, onOpen]);

  return null;
};

const PopoverLayout: React.FC<React.PropsWithChildren<PopoverLayoutProps>> = ({
  children,
  className,
  error,
  countries,
  handleClick,
  handleSearch,
  isLoading,
  label,
  searchText,
  setSearchText,
  value,
}) => {
  const isKiosk = useSelector(isLocalKiosk);

  const anchorPosition = isKiosk ? 'top start' : 'bottom start';

  const handleOpen = React.useCallback(() => {
    setSearchText('');
  }, [setSearchText]);

  return (
    <HeadlessPopover className={classNames('relative', className)} >
      {({ open, close }) => {
        const Icon = open ? ChevronUpIcon : ChevronDownIcon;
        return (
          <>
            <PopoverStateWatcher open={open} onOpen={handleOpen} />
            {isLoading && <div className="w-32 h-14 flex items-center justify-center">
              <Spinner />
            </div>}
            {!isLoading && <PopoverButton className="relative z-0 group rounded-input w-full min-w-32">
              <div
                className={classNames('input focus:outline-none bg-input-bg mr-4 kiosk:mr-8', {
                  'border-danger-default focus:border-danger-default': error,
                  'pt-6': label,
                })}
              >
                {children}
              </div>
              {label && (
                <label className={classNames('input-label left-0 right-auto', { '!text-danger-default': error })}>
                  {label}
                </label>
              )}
              <div className="absolute top-5 right-1 kiosk:top-11 kiosk:right-10">
                <Icon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
            </PopoverButton>}
            <PopoverPanel
              anchor={anchorPosition}
              className={classNames(
                'z-20 w-1/2-screen bg-white h-[320px] rounded border border-[#CED6DD] shadow-[0_4px_3px_0_rgba(0,0,0,0.07)]',
                { 'mb-4': isKiosk, 'mt-4': !isKiosk }
              )}
            >
              <CountryListContent
                close={close}
                countries={countries}
                onClick={handleClick}
                onSearch={handleSearch}
                isLoading={isLoading}
                searchText={searchText}
                setSearchText={setSearchText}
                value={value}
              />
            </PopoverPanel>
          </>
        );
      }}
    </HeadlessPopover>
  );
};

export default PopoverLayout;
